import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { servicesAPI, timeSlotsAPI, bookingsAPI } from '../utils/api';
import CustomerSearch from './CustomerSearch';
import FiveDayCalendar from './FiveDayCalendar';
import dayjs from 'dayjs';

function AdminBookingModal({ isOpen, onClose, onSuccess, preSelectedCustomer = null, preSelectedService = null }) {
  const [selectedCustomer, setSelectedCustomer] = useState(preSelectedCustomer);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(preSelectedService);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarStartDate, setCalendarStartDate] = useState(() => dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    if (isOpen) {
      fetchServices();
      setSelectedCustomer(preSelectedCustomer);
      setSelectedService(preSelectedService);
      setSelectedDate('');
      setSelectedSlot(null);
      setNotes('');
    }
  }, [isOpen, preSelectedCustomer, preSelectedService]);

  useEffect(() => {
    if (selectedDate && selectedService) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedService]);

  useEffect(() => {
    if (selectedCustomer) {
      checkForSavedCard();
    }
  }, [selectedCustomer]);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.filter(s => s.active));
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const response = await timeSlotsAPI.getAvailable(selectedDate, selectedService.duration);
      setAvailableSlots(response.data);
    } catch (error) {
      toast.error('Failed to load available slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const checkForSavedCard = async () => {
    try {
      const response = await bookingsAPI.checkCard(selectedCustomer.email);
      setHasSavedCard(response.data.has_saved_card);
      setUseSavedCard(response.data.has_saved_card);
    } catch (error) {
      console.error('Error checking for saved card:', error);
      setHasSavedCard(false);
      setUseSavedCard(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setLoading(true);
    try {
      await bookingsAPI.adminCreate({
        customer_id: selectedCustomer.id,
        service_id: selectedService.id,
        date: selectedDate,
        start_time: selectedSlot.start_time,
        notes,
        use_saved_card: useSavedCard && hasSavedCard
      });

      toast.success('Booking created successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>
            {preSelectedCustomer ? 'Create Booking' : 'Create New Booking'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Link to recurring booking page */}
        <div style={{
          padding: '0.75rem 1.5rem',
          background: '#F5F2EF',
          borderBottom: '1px solid #E8E4E0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ color: '#6B6B6B', fontSize: '0.875rem' }}>
            🔄 Need recurring appointments?
          </span>
          <Link
            to="/admin/booking/create"
            onClick={onClose}
            style={{
              color: '#8B2635',
              fontWeight: 600,
              fontSize: '0.875rem',
              textDecoration: 'underline'
            }}
          >
            Create Recurring Booking →
          </Link>
        </div>

        {/* Content */}
        <div className="modal-body">
          {/* Customer Selection */}
          {!preSelectedCustomer && (
            <div className="form-group">
              <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                Customer *
              </label>
              <CustomerSearch
                onSelect={setSelectedCustomer}
                selectedCustomer={selectedCustomer}
              />
              {selectedCustomer && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: '#FAF8F5',
                  borderRadius: '6px',
                  border: '1px solid #E8E4E0'
                }}>
                  <strong>{selectedCustomer.name}</strong>
                  <div style={{ fontSize: '0.9rem', color: '#6B6B6B', marginTop: '0.25rem' }}>
                    {selectedCustomer.email} • {selectedCustomer.phone}
                  </div>
                </div>
              )}
            </div>
          )}

          {preSelectedCustomer && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: '#FAF8F5',
              borderRadius: '6px',
              border: '1px solid #E8E4E0'
            }}>
              <strong>{preSelectedCustomer.name}</strong>
              <div style={{ fontSize: '0.9rem', color: '#6B6B6B', marginTop: '0.25rem' }}>
                {preSelectedCustomer.email}
              </div>
            </div>
          )}

          {/* Service Selection */}
          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
              Service *
            </label>
            <select
              className="form-control"
              value={selectedService?.id || ''}
              onChange={(e) => {
                const service = services.find(s => s.id === parseInt(e.target.value));
                setSelectedService(service);
                setSelectedSlot(null);
              }}
              required
            >
              <option value="">Select a service</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} - £{service.price} ({service.duration} min)
                </option>
              ))}
            </select>
          </div>

          {/* Date Selection - 5-Day Calendar */}
          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
              Date *
            </label>
            <div style={{
              border: '1px solid #E8E4E0',
              borderRadius: '8px',
              padding: '0.75rem',
              background: '#fff'
            }}>
              <FiveDayCalendar
                selectedDate={selectedDate}
                onDateSelect={(date) => {
                  setSelectedDate(date);
                  setSelectedSlot(null);
                }}
                startDate={calendarStartDate}
                onNavigate={(days) => setCalendarStartDate(prev => dayjs(prev).add(days, 'day').format('YYYY-MM-DD'))}
                compact={true}
              />
            </div>
            {selectedDate && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: '#FAF8F5',
                borderRadius: '4px',
                borderLeft: '3px solid #8B2635',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#8B2635'
              }}>
                {dayjs(selectedDate).format('dddd, D MMMM YYYY')}
              </div>
            )}
          </div>

          {/* Time Slot Selection */}
          {selectedDate && selectedService && (
            <div className="form-group">
              <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                Time Slot *
              </label>
              {loadingSlots ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#6B6B6B' }}>
                  Loading available slots...
                </div>
              ) : availableSlots.length === 0 ? (
                <div style={{
                  padding: '0.75rem',
                  background: '#FFF8E1',
                  border: '1px solid #E09F3E',
                  borderRadius: '6px',
                  color: '#8B6914'
                }}>
                  No available slots for this date. Please choose another date.
                </div>
              ) : (
                <div className="time-slots-grid">
                  {availableSlots.map((slot, index) => (
                    <div
                      key={`${slot.start_time}-${index}`}
                      className={`time-slot ${selectedSlot?.start_time === slot.start_time ? 'selected' : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      <div>{slot.start_time}</div>
                      <small style={{ fontSize: '0.85rem', opacity: 0.8 }}>to {slot.end_time}</small>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
              Notes (optional)
            </label>
            <textarea
              className="form-control"
              rows="2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this booking..."
            />
          </div>

          {/* Saved Card Option */}
          {hasSavedCard && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#F0F7F1',
              borderRadius: '8px',
              border: '1px solid #A8C9AD'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={useSavedCard}
                  onChange={(e) => setUseSavedCard(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span style={{ color: '#4A7C59', fontSize: '0.9rem' }}>
                  Use customer's saved card for no-show protection
                </span>
              </label>
            </div>
          )}

          {/* Summary */}
          {selectedCustomer && selectedService && selectedSlot && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#FAF8F5',
              borderRadius: '8px',
              border: '1px solid #E8E4E0'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Booking Summary</div>
              <div style={{ fontSize: '0.9rem', color: '#6B6B6B' }}>
                <div><strong>Service:</strong> {selectedService.name}</div>
                <div><strong>Date:</strong> {selectedDate}</div>
                <div><strong>Time:</strong> {selectedSlot.start_time}</div>
                <div style={{ marginTop: '0.5rem', fontSize: '1.1rem', fontWeight: '600', color: '#2C2C2C' }}>
                  Total: £{selectedService.price}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !selectedCustomer || !selectedService || !selectedSlot}
          >
            {loading ? 'Creating...' : 'Create Booking'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminBookingModal;
